import { Type } from '@angular/core';
import { PERMISSION_OPTIONS } from './space.constants';
import {
  FieldTextIconComponent,
  FieldLongTextIconComponent,
  FieldNumberIconComponent,
  FieldDateIconComponent,
  FieldTagIconComponent,
  FieldDropdownIconComponent,
  FieldPeopleIconComponent,
  FieldLinkIconComponent,
  FieldMultiSelectIconComponent,
  FieldFormulaIconComponent,
} from '@tasks-web/ui-components';

/** Available permission level options for table dropdown (re-export from space). */
export const TABLE_PERMISSION_OPTIONS = PERMISSION_OPTIONS;

/** A single default-field option shown in the multi-select checklist. */
export interface TableDefaultFieldOption {
  value: string;
  label: string;
  icon?: Type<unknown>;
}

/**
 * Static list of default field types available when creating a table.
 * These are shown as a multi-select checkbox dropdown in the Create Table form.
 */
export const TABLE_DEFAULT_FIELD_OPTIONS: TableDefaultFieldOption[] = [
  { value: 'number', label: 'Number', icon: FieldNumberIconComponent },
  { value: 'text', label: 'Text', icon: FieldTextIconComponent },
  { value: 'people', label: 'People', icon: FieldPeopleIconComponent },
  { value: 'long_text', label: 'Long text', icon: FieldLongTextIconComponent },
  { value: 'date', label: 'Date', icon: FieldDateIconComponent },
  { value: 'dropdown', label: 'Dropdown', icon: FieldDropdownIconComponent },
  { value: 'tag', label: 'Tag', icon: FieldTagIconComponent },
  {
    value: 'multi_select',
    label: 'Multi Select',
    icon: FieldMultiSelectIconComponent,
  },
  { value: 'formula', label: 'Formula', icon: FieldFormulaIconComponent },
  { value: 'link', label: 'Link', icon: FieldLinkIconComponent },
];

// ── Table Field (column) configuration ───────────────────────────────────────

/**
 * All field types supported by the Add/Edit Field dialog.
 *
 * Per spec 007 §scope, 7 types shipped in MVP.
 * Per spec 014, 4 additional types are added: multi_select, formula, link, people.
 */
export type TableFieldType =
  | 'text'
  | 'long_text'
  | 'number'
  | 'date'
  | 'tag'
  | 'dropdown'
  | 'people'
  | 'multi_select'
  | 'formula'
  | 'link';

/** Option shown in the "Type" dropdown of the Add/Edit Field dialog. */
export interface TableFieldTypeOption {
  value: TableFieldType;
  label: string;
  icon?: Type<unknown>;
}

/**
 * Exhaustive list of field types available in the Add/Edit Field drawer.
 * Order matches the design system dropdown order.
 */
export const TABLE_FIELD_TYPE_OPTIONS: TableFieldTypeOption[] = [
  { value: 'text', label: 'Text', icon: FieldTextIconComponent },
  { value: 'long_text', label: 'Long text', icon: FieldLongTextIconComponent },
  { value: 'number', label: 'Number', icon: FieldNumberIconComponent },
  { value: 'date', label: 'Date', icon: FieldDateIconComponent },
  { value: 'tag', label: 'Tag', icon: FieldTagIconComponent },
  { value: 'dropdown', label: 'Dropdown', icon: FieldDropdownIconComponent },
  { value: 'people', label: 'People', icon: FieldPeopleIconComponent },
  {
    value: 'multi_select',
    label: 'Multi Select',
    icon: FieldMultiSelectIconComponent,
  },
  { value: 'formula', label: 'Formula', icon: FieldFormulaIconComponent },
  { value: 'link', label: 'Link', icon: FieldLinkIconComponent },
];

/** Field types that expose a configurable options list (Tag, Dropdown, Multi Select). */
export const TABLE_FIELD_TYPES_WITH_OPTIONS: readonly TableFieldType[] = [
  'tag',
  'dropdown',
  'multi_select',
] as const;

/** Returns true when the given field type is always read-only (formula). */
export function isReadOnlyFieldType(type: TableFieldType): boolean {
  return type === 'formula';
}

/** Returns true when the given field type supports a configurable options list. */
export function hasOptionsFieldType(type: TableFieldType): boolean {
  return (TABLE_FIELD_TYPES_WITH_OPTIONS as readonly string[]).includes(type);
}

/** Maximum number of options a Tag / Dropdown / Multi Select field can define. */
export const TABLE_FIELD_OPTIONS_MAX = 10;

/**
 * Color palette auto-assigned (and cycled) to Tag options.
 * Matches the design-system swatches shown in the Add Field drawer.
 */
export const TABLE_FIELD_TAG_COLORS: readonly string[] = [
  '#2563EB', // blue
  '#111827', // black
  '#16A34A', // green
  '#F59E0B', // amber
  '#DC2626', // red
  '#3B82F6', // light blue
  '#6B7280', // gray
  '#22C55E', // light green
  '#EF4444', // light red
  '#0EA5E9', // sky
] as const;

/** Backend hard cap for per_page (CapPerPage middleware rejects values above this with 422). */
export const ALL_ROWS_PAGE_SIZE = 150;

/**
 * Error token strings returned by the backend formula engine.
 * Shared by TableCellFormulaComponent and MetadataFieldRendererComponent.
 */
export const FORMULA_ERROR_TOKENS = new Set([
  '#DIV/0!',
  '#REF!',
  '#NAME?',
  '#VALUE!',
  '#NULL!',
  '#N/A',
  '#NUM!',
]);

// ── Cell-level limits ────────────────────────────────────────────────────────

export const TABLE_CELL_LIMITS = {
  text: { maxLen: 255, displayTruncate: 50 },
  longText: { displayTruncate: 200 },
  link: { maxLen: 2048 },
} as const;
